import React, { useState, useEffect } from 'react';
import { useTranslation } from '../i18n/useTranslation';

const GOLD = '#C9A84C';

export default function AboutSlideout({ open, onClose }) {
  const { t } = useTranslation();
  const [visible, setVisible] = useState(false);
  const [shouldRender, setShouldRender] = useState(false);

  useEffect(() => {
    if (open) {
      setShouldRender(true);
      requestAnimationFrame(() => requestAnimationFrame(() => setVisible(true)));
    } else {
      setVisible(false);
      const timer = setTimeout(() => setShouldRender(false), 350);
      return () => clearTimeout(timer);
    }
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const handleKey = (e) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', handleKey);
    return () => window.removeEventListener('keydown', handleKey);
  }, [open, onClose]);

  if (!shouldRender) return null;

  return (
    <>
      <div
        onClick={onClose}
        style={{
          position: 'fixed', inset: 0, zIndex: 998,
          background: `rgba(0,0,0,${visible ? 0.5 : 0})`,
          backdropFilter: visible ? 'blur(4px)' : 'none',
          WebkitBackdropFilter: visible ? 'blur(4px)' : 'none',
          transition: 'background 0.3s ease, backdrop-filter 0.3s ease',
        }}
      />
      <div
        style={{
          position: 'fixed', top: 0, right: 0, bottom: 0,
          width: '100%', maxWidth: 460, zIndex: 999,
          transform: visible ? 'translateX(0)' : 'translateX(100%)',
          transition: 'transform 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
          background: 'var(--bg-primary)',
          borderLeft: '1px solid var(--border-light)',
          display: 'flex', flexDirection: 'column',
          boxShadow: '-8px 0 32px rgba(0,0,0,0.3)',
        }}
      >
        {/* Header */}
        <div style={{
          display: 'flex', alignItems: 'center', gap: 12,
          padding: '20px 20px 16px', paddingTop: 'max(20px, env(safe-area-inset-top, 20px))', borderBottom: '1px solid var(--border-light)',
          flexShrink: 0,
        }}>
          <button
            onClick={onClose}
            style={{
              width: 36, height: 36, borderRadius: '50%', border: 'none',
              background: 'var(--bg-secondary)',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              color: 'var(--text-secondary)', cursor: 'pointer',
              transition: 'background 0.15s',
            }}
            title={t('common.close') || 'Close'}
          >
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M18 6L6 18"/><path d="M6 6l12 12"/></svg>
          </button>
          <h1 style={{ fontSize: 20, fontWeight: 700, color: 'var(--text-primary)', letterSpacing: '-0.01em' }}>
            {t('common.about')}
          </h1>
        </div>

        {/* Content */}
        <div style={{ flex: 1, overflowY: 'auto', padding: '24px 24px 40px' }} className="no-scrollbar">
          {/* Maestro wordmark */}
          <div style={{ textAlign: 'center', marginBottom: 28 }}>
            <div style={{ fontSize: 32, fontWeight: 800, letterSpacing: '-0.03em', color: GOLD }}>
              Maestro
            </div>
          </div>

          {/* Decorative divider */}
          <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 28 }}>
            <div style={{ flex: 1, height: 1, background: `linear-gradient(to right, transparent, ${GOLD}40)` }} />
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke={GOLD} strokeWidth="1.5">
              <path d="M9 18V5l12-2v13" /><circle cx="6" cy="18" r="3" /><circle cx="18" cy="16" r="3" />
            </svg>
            <div style={{ flex: 1, height: 1, background: `linear-gradient(to left, transparent, ${GOLD}40)` }} />
          </div>

          {/* Essay */}
          <div style={{ fontSize: 14.5, lineHeight: 1.8, color: 'var(--text-secondary)' }}>
            <p style={{ marginBottom: 18 }}>
              Sometimes the best projects come out of frustration.
            </p>
            <p style={{ marginBottom: 18 }}>
              I've been a streaming and network playback fan for years. We were pioneers
              in this space — first with the Network Bridge for the DirectStream DAC, then with the
              remarkable AirLens. We were right there at the beginning with Roon as one of their
              founding supporters. We even spent a few years and many hundreds of thousands of dollars
              building our own music manager called Octave, but it proved too big a mountain to climb
              alongside all the other hardware and software work we had going.
            </p>
            <p style={{ marginBottom: 18 }}>
              Still, as a devoted user of JRiver, Audirvana, Roon, Spotify, and the rest, I've had
              the same love-hate relationship you probably have. We love the access to millions of
              tracks on Qobuz. We love the wealth of information and — sometimes — the
              ease of use. But then we hit the walls. The things they can't do. The things that
              frustrate us. The cost of dedicating a computer or server to them. Why can't it just
              be simple, beautiful, and magical?
            </p>
            <p style={{ marginBottom: 18, fontStyle: 'italic', color: GOLD }}>
              How we all wish for the perfect program.
            </p>
            <p style={{ marginBottom: 18 }}>
              That's how Maestro began. A personal passion project to build exactly what <em style={{ fontStyle: 'italic' }}>I</em> want.
              Easy to use, robust, something I can talk to — a program that feels magical.
              One that knows me, learns about me, and grows with me. A place where my own music
              library and NAS sit right alongside the world's streaming services, all in one
              seamless experience. No computer necessary. A service I can use anywhere in the
              world. Something that makes me smile.
            </p>
            <p style={{ marginBottom: 18 }}>
              My friend Seth Godin said, "Wouldn't it be great if you could just ask for whatever
              you wanted it to play or build for you and it did it?" That sparked a fire in me.
              I set out to build Maestro my way. Every high-end audiophile demo track at my
              fingertips. A living playlist that starts where I want and ends where I want and
              never plays tracks it knows I don't like. Personal. Rich. Magical.
            </p>
            <p style={{ marginBottom: 0 }}>
              I built Maestro for me. I wrote all the code myself — no team — with the remarkable
              help of my partner Claude. If it speaks to you the way it speaks to me, welcome.
              You're exactly who I had in mind.
            </p>
          </div>

          {/* Signature */}
          <div style={{
            marginTop: 28, paddingTop: 20,
            borderTop: `1px solid var(--border-light)`,
            display: 'flex', alignItems: 'center', gap: 14,
          }}>
            <div>
              <a href="mailto:paul@maestromusic.app" style={{ fontSize: 15, fontWeight: 700, color: GOLD, textDecoration: 'none' }}>
                Paul McGowan
              </a>
            </div>
          </div>

          {/* Footer */}
          <div style={{
            marginTop: 28, padding: 16, borderRadius: 14,
            background: `rgba(201,168,76,0.06)`,
            border: `1px solid rgba(201,168,76,0.15)`,
            textAlign: 'center',
          }}>
            <div style={{ fontSize: 12, color: 'var(--text-tertiary)', lineHeight: 1.5 }}>
              maestromusic.app
            </div>
          </div>
        </div>
      </div>
    </>
  );
}
